import { FC, FormEvent, useRef, useState } from 'react';
import { Button, Text, TextInput } from '@gravity-ui/uikit';
import { ArrowUpFromLine } from '@gravity-ui/icons';
import { VideoList, VideoListUpdateAction } from '../VideoList/VideoList';
import { VideoInfo } from '../../types/video/VideoInfo';
import { ProfilesRecord } from '../Page/types/ProfilesRecord';
import { MobileBottomBar } from './components/MobileBottomBar';
import { MobileUploadView } from './components/MobileUploadView';
import { useDynamicPageSize } from './hooks/useDynamicPageSize';
import { useMobileUpload } from './hooks/useMobileUpload';
import { useDebounce } from '../../hooks/useDebounce';
import { MAX_VIDEO_TITLE_LENGTH } from '../../constants/MAX_VIDEO_TITLE_LENGTH';
import { pageT } from '../../i18n';
import './styles/MobilePage.css';

type MobilePageProps = {
    videos: VideoInfo[];
    pagination: { page: number; pageSize: number };
    totalItems: number;
    profiles: ProfilesRecord;
    highlightedVideoId?: string | null;
    filterTitle: string;
    onVideoListUpdate: (action: VideoListUpdateAction) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onFilterTitleChange: (title: string) => void;
    onUpload: () => void;
    onUploadStart?: (uploadUrl: string) => void;
};

export const MobilePage: FC<MobilePageProps> = ({
    videos,
    pagination,
    totalItems,
    profiles,
    highlightedVideoId,
    filterTitle,
    onVideoListUpdate,
    onPageChange,
    onPageSizeChange,
    onFilterTitleChange,
    onUpload,
    onUploadStart,
}) => {
    const [isUploadMode, setIsUploadMode] = useState(false);
    const [searchValue, setSearchValue] = useState(filterTitle);
    const videoListContainerRef = useRef<HTMLDivElement>(null);

    const totalPages = Math.ceil(totalItems / pagination.pageSize) || 1;

    // Дебаунс поиска
    const debouncedFilterChange = useDebounce((title: string) => {
        onFilterTitleChange(title);
    }, 400);

    const handleSearchChange = (value: string) => {
        setSearchValue(value);
        debouncedFilterChange(value);
    };

    // Динамический pageSize по высоте контейнера
    useDynamicPageSize({
        containerRef: videoListContainerRef,
        currentPageSize: pagination.pageSize,
        onPageSizeChange,
        videosCount: videos.length,
    });

    // Логика загрузки видео
    const upload = useMobileUpload({
        profiles,
        onUpload: () => {
            onUpload();
            setIsUploadMode(false);
        },
        onUploadStart,
    });

    const handleOpenUpload = () => {
        upload.openModal();
        setIsUploadMode(true);
    };

    const handleCancelUpload = () => {
        upload.handleCancel();
        setIsUploadMode(false);
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        upload.handleSubmit();
    };

    const canSubmit =
        Boolean(upload.uploadState.file) &&
        Boolean(upload.uploadState.profile) &&
        upload.uploadState.videoTitle.length <= MAX_VIDEO_TITLE_LENGTH;

    return (
        <form className="mobile-page" onSubmit={handleSubmit} autoComplete="off">
            {/* Верхняя строка */}
            {isUploadMode ? (
                /* Режим загрузки: заголовок показываем только когда файл уже выбран */
                upload.uploadState.file ? (
                    <div className="mobile-page__header-upload">
                        <Text variant="subheader-3">{pageT('videoUploadFull')}</Text>
                    </div>
                ) : null
            ) : (
                /* Режим списка: поиск + кнопка загрузки */
                <div className="mobile-page__search-row">
                    <TextInput
                        size="l"
                        placeholder={pageT('searchByTitle')}
                        value={searchValue}
                        onUpdate={handleSearchChange}
                        hasClear
                    />
                    <Button
                        view="action"
                        size="l"
                        title={pageT('videoUploadShort')}
                        onClick={handleOpenUpload}
                        className="mobile-page__upload-btn"
                    >
                        <ArrowUpFromLine />
                    </Button>
                </div>
            )}

            {/* Основной контент — список или форма загрузки */}
            <div className="mobile-page__content" ref={videoListContainerRef}>
                {isUploadMode ? (
                    <MobileUploadView
                        uploadState={upload.uploadState}
                        profiles={profiles}
                        onFileSelected={upload.handleFileSelected}
                        onChangeName={(value) => upload.updateField('videoTitle', value)}
                        onChangeProfile={(value) => upload.updateField('profile', value)}
                        onToggleSummarization={() =>
                            upload.updateField(
                                'enableSummarization',
                                !upload.uploadState.enableSummarization,
                            )
                        }
                    />
                ) : (
                    <VideoList
                        videos={videos}
                        onUpdate={onVideoListUpdate}
                        isMobile={true}
                        profiles={profiles}
                        highlightedVideoId={highlightedVideoId}
                    />
                )}
            </div>

            {/* Нижняя панель */}
            {isUploadMode ? (
                /* Режим загрузки: Загрузить | Отмена */
                <MobileBottomBar
                    mode="upload"
                    isUploading={upload.uploadState.isUploading}
                    canSubmit={canSubmit}
                    onSubmit={upload.handleSubmit}
                    onCancel={handleCancelUpload}
                />
            ) : (
                /* Режим списка: ← | страница X из N | → */
                <div className="mobile-bottom-bar">
                    <Button
                        className="mobile-bottom-bar__btn"
                        view="outlined"
                        size="l"
                        disabled={pagination.page <= 1}
                        onClick={() => onPageChange(pagination.page - 1)}
                    >
                        ←
                    </Button>
                    <Text
                        variant="body-2"
                        color="secondary"
                        className="mobile-bottom-bar__page-info"
                    >
                        {pagination.page} / {totalPages}
                    </Text>
                    <Button
                        className="mobile-bottom-bar__btn"
                        view="outlined"
                        size="l"
                        disabled={pagination.page >= totalPages}
                        onClick={() => onPageChange(pagination.page + 1)}
                    >
                        →
                    </Button>
                </div>
            )}
        </form>
    );
};
